import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from '../i18n/useTranslation';
import { getTrendAnalysis } from '../services/geminiService';
import type { ChatMessage } from '../types';
import { Trash2Icon, XIcon, SendIcon } from './icons';

interface TrendSpotterProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
}

const TrendSpotter: React.FC<TrendSpotterProps> = ({ isOpen, setIsOpen }) => {
    const { t } = useTranslation();
    
    const [messages, setMessages] = useState<ChatMessage[]>(() => {
        try {
            const savedMessages = localStorage.getItem('trendSpotterHistory');
            return savedMessages ? JSON.parse(savedMessages) : [
                { id: `model-${Date.now()}`, role: 'model', text: t('trendSpotter.greeting') }
            ];
        } catch (error) {
            console.error("Failed to parse chat history from localStorage", error);
            return [{ id: `model-${Date.now()}`, role: 'model', text: t('trendSpotter.greeting') }];
        }
    });

    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const chatEndRef = useRef<HTMLDivElement>(null);

    const scrollToBottom = () => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        if (isOpen) {
            scrollToBottom();
        }
    }, [messages, isOpen]);
    
    useEffect(() => {
        localStorage.setItem('trendSpotterHistory', JSON.stringify(messages));
    }, [messages]);
    
    const handleClearHistory = () => {
        setMessages([{ id: `model-${Date.now()}`, role: 'model', text: t('trendSpotter.cleared') }]);
    };

    const handleSendMessage = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!input.trim() || isLoading) return;

        const userMessage: ChatMessage = { id: `user-${Date.now()}`, role: 'user', text: input };
        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setIsLoading(true);

        try {
            const responseText = await getTrendAnalysis(input);
            const modelMessage: ChatMessage = { id: `model-${Date.now()}`, role: 'model', text: responseText, isError: responseText.startsWith("Could not find API Key") || responseText.startsWith("An error occurred") };
            setMessages(prev => [...prev, modelMessage]);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "An unknown error occurred.";
            const modelMessage: ChatMessage = { id: `model-error-${Date.now()}`, role: 'model', text: `Sorry, an error occurred: ${errorMessage}`, isError: true };
            setMessages(prev => [...prev, modelMessage]);
        }
        
        setIsLoading(false);
    };
    
    if (!isOpen) return null;

    return (
        <div className="fixed bottom-24 right-6 w-full max-w-sm h-[60vh] bg-white dark:bg-[#1c1c1c] rounded-lg shadow-2xl flex flex-col z-50 animate-fade-in-up">
            <header className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                <h3 className="font-serif-display text-xl text-gray-900 dark:text-white">Elysian Trend Spotter</h3>
                <div className="flex items-center space-x-2">
                    <button onClick={handleClearHistory} title="Clear Chat History" className="text-gray-400 hover:text-gray-800 dark:hover:text-white transition-colors">
                       <Trash2Icon className="w-5 h-5" />
                    </button>
                    <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-gray-800 dark:hover:text-white">
                        <XIcon className="w-6 h-6" />
                    </button>
                </div>
            </header>
            <main className="flex-1 p-4 overflow-y-auto bg-gray-50 dark:bg-transparent">
                <div className="space-y-4">
                    {messages.map((msg) => (
                        <div key={msg.id} className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'} animate-fade-in-up`}>
                            <div className={`max-w-[85%] p-3 rounded-lg ${
                                msg.role === 'user'
                                ? 'bg-amber-400 text-black'
                                : msg.isError
                                ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                                : 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white'
                            }`}>
                                <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                            </div>
                        </div>
                    ))}
                    {isLoading && (
                        <div className="flex justify-start animate-fade-in-up">
                            <div className="bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white p-3 rounded-lg flex items-center space-x-2">
                               <span className="text-sm">{t('trendSpotter.thinking')}</span>
                               <div className="w-2 h-2 bg-amber-400 dark:bg-amber-300 rounded-full animate-pulse"></div>
                               <div className="w-2 h-2 bg-amber-400 dark:bg-amber-300 rounded-full animate-pulse delay-200"></div>
                               <div className="w-2 h-2 bg-amber-400 dark:bg-amber-300 rounded-full animate-pulse delay-400"></div>
                            </div>
                        </div>
                    )}
                    <div ref={chatEndRef} />
                </div>
            </main>
            <footer className="p-4 border-t border-gray-200 dark:border-gray-700">
                <form onSubmit={handleSendMessage} className="flex items-center space-x-2">
                    <input 
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder={t('trendSpotter.placeholder') as string}
                        className="flex-1 bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 rounded-full py-2 px-4 focus:ring-amber-400 focus:border-amber-400 transition"
                        disabled={isLoading}
                    />
                    <button type="submit" className="p-2 rounded-full bg-amber-400 text-black disabled:bg-gray-600 transition" disabled={isLoading || !input.trim()}>
                        <SendIcon className="w-6 h-6" />
                    </button>
                </form>
            </footer>
        </div>
    );
};

export default TrendSpotter;